import { requestReorderDevice, sortByPosition } from 'src/components/Lists/list-helpers';
import { groups } from 'src/stores/lists/groups';
import { rooms } from 'src/stores/lists/rooms';
import { alarms, temp_schemes } from 'src/stores/lists/scenes-derived';
import { routeParams } from 'src/stores/ui/viewport';
import { roomsGroupsFilter, roomsScenesFilter, thermostatsFilter } from 'src/stores/ui/filters';
import { scenes } from 'src/stores/lists/scenes';
import type { Group } from 'src/types/Group';
import type { ISceneObj } from 'src/types/SceneType';
import { format } from 'svelte-i18n';
import { get } from 'svelte/store';
import { location, push } from 'svelte-spa-router';
import type { Room } from 'src/types/Room';

type StoreTypes = 'rooms' | 'groups' | 'scenes' | 'alarms' | 'thermostats';

type RequestTypes = 'room' | 'group' | 'scene' | 'alarm' | 'temp_scheme';

/** Entity types used for room-scoped list filters (groups / scenes / thermostats). */
export type RoomKeyedListItem = Group | ISceneObj;

/** Store keys that support {@link setListRoomFilter}. */
export type RoomFilterStoreType = Extract<StoreTypes, 'groups' | 'scenes' | 'thermostats'>;

/** Items passed into {@link orderItemsInRoom} from wizards and room flows. */
export type OrderInRoomItem = RoomKeyedListItem | (Partial<Room> & { new?: boolean });

const REQUEST_TYPES: { [key in StoreTypes]: RequestTypes } = {
	rooms: 'room',
	groups: 'group',
	scenes: 'scene',
	alarms: 'scene',
	thermostats: 'scene',
};

type StoreUpdateItem = Group | ISceneObj | Partial<Room>;

export const itemsStoreUpdaters: Partial<Record<StoreTypes, (item: StoreUpdateItem) => void>> = {
	groups: (item) => groups?.update(item as Group),
	scenes: (item) => scenes?.editScene(item as ISceneObj),
	alarms: (item) => scenes?.editScene(item as ISceneObj),
	rooms: (item) => rooms?.update(item),
	thermostats: (item) => scenes?.editScene(item as ISceneObj),
};

export const clearItemNewFlagFromStore: Partial<Record<StoreTypes, (id: string | number) => void>> = {
	rooms: (id) => rooms?.clearNewFlag(id),
	groups: (id) => groups?.clearNewFlag(id),
	scenes: (id) => scenes?.clearNewFlag(id),
	alarms: (id) => scenes?.clearNewFlag(id),
	thermostats: (id) => scenes?.clearNewFlag(id),
};

export const getItemRoomID: Partial<Record<StoreTypes, (item: RoomKeyedListItem) => string | number>> = {
	groups: (item) => (item as Group).room_id,
	scenes: (item) => (item as ISceneObj)._meta.room,
	alarms: (item) => (item as ISceneObj)._meta.room,
	thermostats: (item) => (item as ISceneObj)._meta.room,
};

type PulseClearItem = {
	new?: boolean;
	id?: string | number;
	_id?: string | number;
	_meta?: { new?: boolean };
};

export const updateItemStoreAfterPulse = (
	item: Group | ISceneObj | (Partial<Room> & { new?: boolean }),
	type: StoreTypes,
) => {
	const p = item as PulseClearItem;
	if (!p?.new && !p?._meta?.new) return;

	const id = p.id ?? p._id;
	if (id === undefined) return;
	clearItemNewFlagFromStore[type]?.(id);
};

export const getOrderedItems: Partial<
	Record<StoreTypes, (item?: OrderInRoomItem) => OrderInRoomItem[]>
> = {
	groups: (item) => {
		const groupItem = item as Group;
		return Object.values(get(groups))
			.filter((group: Group) => String(group.room_id) === String(groupItem.room_id))
			.sort(sortByPosition);
	},
	scenes: (item) => {
		const sceneItem = item as ISceneObj;
		return Object.values(get(scenes))
			.filter((scene: ISceneObj) => String(scene._meta.room) === String(sceneItem._meta.room))
			.sort((x: ISceneObj, y: ISceneObj) => {
				return x._meta.position - y._meta.position;
			});
	},
	alarms: (item) => {
		const alarmItem = item as ISceneObj;
		return Object.values(get(alarms))
			.filter((alarm: ISceneObj) => String(alarm._meta.room) === String(alarmItem._meta.room))
			.sort((x: ISceneObj, y: ISceneObj) => {
				return x._meta.position - y._meta.position;
			});
	},
	thermostats: (item) => {
		const schemeItem = item as ISceneObj;
		return Object.values(get(temp_schemes))
			.filter((scheme: ISceneObj) => String(scheme._meta.room) === String(schemeItem._meta.room))
			.sort((x: ISceneObj, y: ISceneObj) => {
				return x._meta.position - y._meta.position;
			});
	},
	rooms: () => Object.values(get(rooms)).sort(sortByPosition),
};

const defaultPositionSetter = (item: OrderInRoomItem, index: number) => ({
	...item,
	position: index + 1,
});

const metaPositionSetter = (item: OrderInRoomItem, index: number) => ({
	...item,
	_meta: {
		...((item as ISceneObj)._meta ?? {}),
		position: index + 1,
	},
});

export const setItemNewPosition: Partial<
	Record<StoreTypes, (item: OrderInRoomItem, index: number) => object>
> = {
	groups: defaultPositionSetter,
	rooms: defaultPositionSetter,
	scenes: metaPositionSetter,
	alarms: metaPositionSetter,
	thermostats: metaPositionSetter,
};

export const orderItemsInRoom = async (item: OrderInRoomItem, type: StoreTypes) => {
	const getOrdered = getOrderedItems[type];
	const setPosition = setItemNewPosition[type];

	if (!getOrdered || !setPosition) return;

	const sameRoomItems = getOrdered(item) || [];

	if (sameRoomItems.length === 0) return;

	// Clear active pulse animations when adding new items fast (before pulse animation end)
	sameRoomItems.forEach((roomitem) => {
		const r = roomitem as PulseClearItem;
		if (r?.new || r?._meta?.new) {
			const id = r?.id ?? r?._id;
			if (id !== undefined) clearItemNewFlagFromStore[type]?.(id);
		}
	});

	// Shift items with one position
	const orderedList: Record<string, OrderInRoomItem> = {};
	sameRoomItems.forEach((roomitem, index) => {
		const r = roomitem as PulseClearItem;
		const rowKey = r?.id ?? r?._id;
		if (rowKey === undefined || rowKey === null) return;

		const updatedItem = setPosition(roomitem, index);
		// Delete from spread copies as safety
		if (type === 'alarms' || type === 'scenes' || type === 'thermostats') {
			delete updatedItem?.['_meta']?.['new'];
		} else {
			delete updatedItem?.['new'];
		}

		orderedList[String(rowKey)] = updatedItem as OrderInRoomItem;
	});

	await requestReorderDevice(REQUEST_TYPES[type], orderedList);
};

export const setListRoomFilter = (item: RoomKeyedListItem, type: RoomFilterStoreType) => {
	if (!getItemRoomID[type]) return;

	const roomId = getItemRoomID[type](item);

	const globalFilter = {
		value: -1,
		title: get(format)('option_global', { default: 'Global' }),
	};

	const roomFilter =
		roomId === globalFilter.value
			? globalFilter
			: { title: get(rooms)[roomId]?.name, value: roomId };

	// Set per-store branch so TS narrows the writable to its concrete shape and preserves
	// store-specific fields (e.g. `type` on `ThermostatsFilters`).
	switch (type) {
		case 'groups':
			roomsGroupsFilter.set({ ...get(roomsGroupsFilter), rooms: [roomFilter] });
			return;
		case 'scenes':
			roomsScenesFilter.set({ ...get(roomsScenesFilter), rooms: [roomFilter] });
			return;
		case 'thermostats':
			thermostatsFilter.set({ ...get(thermostatsFilter), rooms: [roomFilter] });
			return;
	}
};

export const itemSavedConfig: Partial<
	Record<
		StoreTypes,
		{
			allRoute: string;
			getIsRoomMatch?: (item: Group | ISceneObj | Partial<Room>) => boolean;
			setRoomFilter?: (item: RoomKeyedListItem) => void;
		}
	>
> = {
	groups: {
		allRoute: '/home/groups/-1',
		getIsRoomMatch: (item) =>
			get(routeParams)?.type === 'room' &&
			get(location).includes(`${(item as Group)?.room_id}/groups`),
		setRoomFilter: (item) => setListRoomFilter(item, 'groups'),
	},
	scenes: {
		allRoute: '/home/scenes/-1',
		getIsRoomMatch: (item) =>
			get(routeParams)?.type === 'room' &&
			get(location).includes(`${(item as ISceneObj)?._meta?.room}/scenes`),
		setRoomFilter: (item) => setListRoomFilter(item, 'scenes'),
	},
	alarms: {
		allRoute: '/home/alarms/-1',
	},
	rooms: {
		allRoute: '/home/rooms/-1',
	},
	thermostats: {
		allRoute: '/home/thermostats/-1',
		getIsRoomMatch: (item) =>
			get(routeParams)?.type === 'room' &&
			get(location).includes(`${(item as ISceneObj)?._meta?.room}/thermostats`),
		setRoomFilter: (item) => setListRoomFilter(item, 'thermostats'),
	},
};

export const clearRoomsItemNewFlag = () => {
	Object.values(get(rooms)).forEach((room: Room & { new?: boolean }) => {
		if (!room?.new) return;
		rooms.clearNewFlag(room.id);
	});
};

/**
 * Pulse (`new` / `_meta.new`) clearing when leaving list screens — groups, thermostats, scenes.
 *
 * - **All lists (`roomId === '-1'`)**: clear pulse for items in the current “all X” scope so leaving
 *   for another main tab does not leave stale highlights.
 * - **Room tab**: clear pulse for items that belong to the **same** room as the tab; **keep** pulse
 *   for items created in **another** room when redirected to “all X”, so the user still sees what
 *   was just added elsewhere.
 * - **Room tab, non-matching section** (e.g. on Scenes tab but created a group): redirect to “all”
 *   still keeps pulse for items not in that tab’s entity type — same “keep when other room/section”
 *   idea as above.
 *
 * @see clearAlarmItemNewFlag — alarms intentionally clear **globally** on leave (documented at call site).
 */
export const clearGroupsItemNewFlag = (roomId: string) => {
	Object.values(get(groups)).forEach((group: Group & { new?: boolean }) => {
		if (!group?.new) return;
		if (roomId === '-1' || String(group.room_id) === String(roomId)) {
			groups.clearNewFlag(group.id);
		}
	});
};

export const clearThermostatsItemNewFlag = (roomId: string) => {
	Object.values(get(scenes)).forEach((scene: ISceneObj) => {
		if (!scene?._meta?.new) return;

		const id = scene._id ?? scene.id;
		const isThermostat = !!scene.tschem;
		if (isThermostat && (roomId === '-1' || String(scene._meta?.room) === String(roomId))) {
			scenes.clearNewFlag(id);
		}
	});
};

export const clearAlarmItemNewFlag = () => {
	Object.values(get(scenes)).forEach((scene: ISceneObj) => {
		if (!scene?._meta?.new) return;
		const id = scene._id ?? scene.id;
		const isAlarm = scene._gui_function === 'alarm';

		if (isAlarm) scenes.clearNewFlag(id);
	});
};

export const clearScenesItemNewFlag = (roomId: string) => {
	Object.values(get(scenes)).forEach((scene: ISceneObj) => {
		if (!scene?._meta?.new) return;

		const id = scene._id ?? scene.id;
		const isAlarm = scene._gui_function === 'alarm';
		const isThermostat = !!scene.tschem;

		if (
			!isAlarm &&
			!isThermostat &&
			(roomId === '-1' || String(scene._meta?.room) === String(roomId))
		) {
			scenes.clearNewFlag(id);
		}
	});
};

export const onItemSaved = (item: ISceneObj | Group | Partial<Room>, type: StoreTypes) => {
	const config = itemSavedConfig[type];
	if (!config) return;

	const { allRoute, getIsRoomMatch, setRoomFilter } = config;

	const isRoomMatch = getIsRoomMatch?.(item) ?? false;

	if (!get(location).includes(allRoute) && !isRoomMatch) {
		push(allRoute);
	}

	if (!isRoomMatch) {
		setRoomFilter?.(item as RoomKeyedListItem);
	}
};
